import { createContext, useContext } from 'react';
import type { BaseShow, SelectedNetwork } from '@/types';

export interface DerivedNetworkContextType {
  /**
   * The primary selected network. In query mode (multi-network content performance) this is the
   * first entry of `selectedNetworks`; in path mode it is the single network from the URL.
   */
  selectedNetwork: SelectedNetwork;
  /**
   * All selected networks. In path mode this is `[selectedNetwork]`; in query mode it carries every
   * network resolved from `?networks=`. Existing consumers can keep reading `selectedNetwork`.
   */
  selectedNetworks: SelectedNetwork[];
  /**
   * Deduped shows across all selected networks, in network (then show) order. In path mode this
   * equals `selectedNetwork.shows`. Prefer this over calling `aggregateShows` at each call site.
   */
  shows: BaseShow[];
}

export const DerivedNetworkContext = createContext<DerivedNetworkContextType | undefined>(
  undefined
);

const useDerivedNetworkContext = () => {
  const context = useContext(DerivedNetworkContext);
  if (!context) {
    throw new Error('useDerivedNetworkContext must be used within a DerivedNetworkProvider');
  }
  return context;
};

export default useDerivedNetworkContext;
